import Vue from "vue";
import Vuex from "vuex";

import { initFullscreenPlugin } from "@chamaileon-sdk/plugin-interface";

import i18n from "@/plugins/i18n";

import router from "@/router/index.js";

import globals from "@/utils/globals";
import cookie from "@/utils/cookie";

import createLoginModule from "./modules/login";
import createForgotPasswordModule from "./modules/forgotPassword";
import createChangePasswordModule from "./modules/changePassword";
import createSsoModule from "./modules/sso";
import createRegisterModule from "./modules/register";
import createEnvironmentModule from "./modules/environment";
import createSetUpAccountModule from "./modules/setUpAccount";
import createActivateAccountModule from "./modules/activateAccount";
import createConfirmEmailModule from "./modules/confirmEmail";
import createAuthSwitchRegistrationModule from "./modules/authSwitchRegistration";
import createTokenModule from "./modules/token";

Vue.use(Vuex);

const parentWindowUrl = location.hostname;
const returnUrlMapping = {
	localhost: "http://localhost:50008",
	"app-staging.chamaileon.io": "https://app-staging.chamaileon.io",
	"app-test.chamaileon.io": "https://app-test.chamaileon.io",
	"app.chamaileon.io": "https://app.chamaileon.io",
	"app-santander.chamaileon.io": "https://app-santander.chamaileon.io",
};
let returnUrl = "https://app.chamaileon.io/";
Object.keys(returnUrlMapping).forEach((key) => {
	if (parentWindowUrl.includes(key)) {
		returnUrl = returnUrlMapping[key];
	}
});
const excludedDomains = [ "app-santander.chamaileon.io" ];

export default new Vuex.Store({
	modules: {
		login: createLoginModule({ globals }),
		forgotPassword: createForgotPasswordModule({ globals }),
		changePassword: createChangePasswordModule({ globals }),
		register: createRegisterModule({ globals }),
		environment: createEnvironmentModule({ globals }),
		setUpAccount: createSetUpAccountModule({ globals }),
		activateAccount: createActivateAccountModule({ globals }),
		confirmEmail: createConfirmEmailModule({ globals }),
		sso: createSsoModule({ globals }),
		authSwitchRegistration: createAuthSwitchRegistrationModule({ globals }),
		token: createTokenModule({ globals }),
	},
	state: {
		queryParams: {},
	},
	actions: {
		setChamaileonCookies(context, tokens) {
			if (tokens.userToken) {
				cookie.removeCookie(tokens.userToken.name);
				cookie.setCookie(tokens.userToken.name, tokens.userToken.token, 10);
			}
			if (tokens.cognitoToken) {
				cookie.removeCookie(tokens.cognitoToken.name);
				cookie.setCookie(tokens.cognitoToken.name, tokens.cognitoToken.token, 10);
			}
			if (tokens.cognitoRefreshToken) {
				cookie.removeCookie(tokens.cognitoRefreshToken.name);
				cookie.setCookie(tokens.cognitoRefreshToken.name, tokens.cognitoRefreshToken.token, 100);
			}
		},
		async initChamaileonSpa({ state, dispatch }) {
			const envName = state.environment.name;
			const envConfig = Vue.prototype.$chamaileon.environmentConfig;

			const pluginVersion = envConfig.plugins.chamaileonSpa;
			const pluginSrc = `${envConfig.pluginUrlTemplates.chamaileonSpa.replace("%version%", pluginVersion)}?env=${envName}`;

			Vue.prototype.$chamaileon.chamaileonSpa = await initFullscreenPlugin(
				{
					data: {
						currentRoute: {
							path: router.currentRoute.path,
							hash: router.currentRoute.hash,
						},
					},
					settings: {
						splashScreenUrl: "https://cdn.chamaileon.io/assets/splashScreen.html",
						domain: location.hostname,
						returnUrl,
						enableGtm: !excludedDomains.some(domain => parentWindowUrl.includes(domain)) && process.env.NODE_ENV !== "development",
					},
					hooks: {
						close() {
							Vue.prototype.$chamaileon.chamaileonSpa.hide();
						},
						async syncRoute(path) {
							if (router.currentRoute.path === path) return;
							await router.replace({ path });
						},
						async hideSplashScreen() {
							await Vue.prototype.$chamaileon.chamaileonSpa.hideSplashScreen();
						},
						checkTokens() {
							return dispatch("token/checkTokens");
						},
						getTokens() {
							return dispatch("token/getTokens");
						},
						integrationOauthLogin({ urlWithQueryString, redirectUri }) {
							return new Promise((resolve, reject) => {
								const authWindow = window.open(urlWithQueryString, "OAuth", "width=900,height=600");

								const timer = setInterval(() => {
									if (authWindow.closed) {
										console.error("OAuth was interrupted: window got closed.");
										// eslint-disable-next-line no-use-before-define, prefer-promise-reject-errors
										shutdown(() => reject(new Error("oauthInterrupted")));
									}
								}, 500);

								const authFinishListener = (event) => {
									if (!redirectUri.includes(event.origin)) return;

									try {
										let OAuthData;

										try {
											OAuthData = JSON.parse(event.data);
										} catch (err) {
											return;
										}

										if (OAuthData.type !== "oauthFinished") return;

										const forwardMsg = { origin: event.origin, OAuthData };

										// eslint-disable-next-line no-use-before-define
										shutdown(() => resolve(forwardMsg));
									} catch (err) {
										console.error("OAuth error:", err);
										// eslint-disable-next-line no-use-before-define
										shutdown(() => reject(err));
									}
								};

								const shutdown = (action) => {
									clearInterval(timer);
									window.removeEventListener("message", authFinishListener);
									authWindow.close();
									action();
								};

								window.removeEventListener("message", authFinishListener);
								window.addEventListener("message", authFinishListener);
							});
						},
						logoutUser: (params) => {
							setTimeout(() => {
								Vue.prototype.$chamaileon.chamaileonSpa.destroy();
								router.push({ name: "Login", query: { logoutText: params.logoutText, ...(params.forceRefresh ? { forceRefresh: true } : {}) } }).catch(() => {});
							}, 500);

							dispatch("token/clearTokens");
						},
						reloadWindow() {
							window.location.reload();
						},
					},
				},
				{
					id: "chamaileon-spa",
					src: pluginSrc,
				},
			);

			await Vue.prototype.$chamaileon.chamaileonSpa.showSplashScreen();
			await Vue.prototype.$chamaileon.chamaileonSpa.show({ x: "-100vw", y: "0px", opacity: 0.5, scale: 1, time: 500 });
		},
		async destroyChamaileonSpa({ dispatch }, { errorCode }) {
			dispatch("token/clearTokens", { root: true });
			await Vue.prototype.$chamaileon.chamaileonSpa.destroy();
			router.push({ name: "Login", query: { logoutText: i18n.t(`errorMessages.${errorCode}`) } }).catch(() => {});
		},
	},
	mutations: {
		SET_QUERY_PARAMS(state, queryParams) {
			state.queryParams = queryParams;
		},
	},
});
